// Platform Admin + School Admin — Best-Hour Heatmap (7×24 IST)
// =============================================================
// Renders a CSS-grid heatmap of WhatsApp 'read' events grouped by
// (weekday, hour) in IST, with a One-Click "Schedule This Week"
// flow that auto-creates N scheduled publishes across the top slots.
import React, { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Color intensity helper — green scale (light → dark) based on share_pct
function cellColor(reads, peakReads) {
  if (!reads || !peakReads) return 'bg-slate-100';
  const intensity = Math.min(reads / peakReads, 1);
  if (intensity >= 0.85) return 'bg-emerald-700 text-white';
  if (intensity >= 0.6) return 'bg-emerald-500 text-white';
  if (intensity >= 0.35) return 'bg-emerald-300 text-emerald-900';
  if (intensity >= 0.15) return 'bg-emerald-200 text-emerald-900';
  return 'bg-emerald-100 text-emerald-900';
}

export default function BestHourHeatmap({ token, language = 'hi', completedJobs = [] }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('30d');
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [slotCount, setSlotCount] = useState(7);
  const [topSlots, setTopSlots] = useState([]);
  const [scheduling, setScheduling] = useState(false);
  const [scheduleResult, setScheduleResult] = useState(null);

  const load = async (r = range) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/veo/analytics/heatmap?range=${r}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await res.json();
      if (j.success) setData(j);
    } catch (e) { console.warn('heatmap load:', e); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openScheduleModal = async () => {
    setShowScheduleModal(true);
    setScheduleResult(null);
    try {
      const res = await fetch(`${API_URL}/api/veo/analytics/heatmap/next-top-slots?count=${slotCount}&range=${range}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await res.json();
      if (j.success) setTopSlots(j.slots || []);
    } catch (e) { console.warn('top slots:', e); }
  };

  const reloadSlots = async (newCount) => {
    setSlotCount(newCount);
    try {
      const res = await fetch(`${API_URL}/api/veo/analytics/heatmap/next-top-slots?count=${newCount}&range=${range}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await res.json();
      if (j.success) setTopSlots(j.slots || []);
    } catch (e) { console.warn('top slots:', e); }
  };

  const scheduleAll = async () => {
    if (!selectedJobId) {
      setScheduleResult({ type: 'error', text: language === 'hi' ? 'पहले एक video चुनें' : 'Pick a video first' });
      return;
    }
    if (topSlots.length === 0) {
      setScheduleResult({ type: 'error', text: language === 'hi' ? 'कोई slot उपलब्ध नहीं' : 'No slots available' });
      return;
    }
    setScheduling(true);
    setScheduleResult(null);
    const results = [];
    for (const slot of topSlots) {
      try {
        const res = await fetch(`${API_URL}/api/veo/publish/schedule/${selectedJobId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            channels: ['instagram'],
            scheduled_at: slot.scheduled_at,
            caption_language: language,
          }),
        });
        const j = await res.json();
        results.push({ slot, ok: res.ok && j.success, publish_id: j.publish_id, error: j.detail });
      } catch (e) {
        results.push({ slot, ok: false, error: e.message });
      }
    }
    const succeeded = results.filter((r) => r.ok).length;
    setScheduleResult({
      type: succeeded === results.length ? 'success' : 'partial',
      text: language === 'hi'
        ? `✅ ${succeeded}/${results.length} schedules बने`
        : `✅ ${succeeded}/${results.length} schedules created`,
      details: results,
    });
    setScheduling(false);
  };

  if (loading && !data) return <div className="p-4 text-gray-500 text-sm">Loading heatmap…</div>;
  if (!data) return null;

  const { cells, peak_reads, sample_size, weekday_labels, weekday_labels_hi, top_slots: ts, note } = data;
  const weekdayLabels = language === 'hi' ? weekday_labels_hi : weekday_labels;

  // Group cells by weekday → hour matrix
  const matrix = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => ({ reads: 0, share_pct: 0 })));
  cells.forEach((c) => { matrix[c.weekday][c.hour] = c; });

  return (
    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-4" data-testid="best-hour-heatmap">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <h4 className="font-bold text-emerald-900 text-sm flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            🗓 {language === 'hi' ? 'Best-Hour Heatmap' : 'Best-Hour Heatmap'}
            <span className="text-[10px] font-normal bg-emerald-200 text-emerald-800 px-2 py-0.5 rounded-full">7×24 IST</span>
          </h4>
          <p className="text-[11px] text-emerald-700 mt-0.5">
            {language === 'hi'
              ? 'WhatsApp read events से derived — गहरा हरा = ज़्यादा reads मिले'
              : 'Derived from WhatsApp read events — darker green = higher engagement'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex bg-white rounded-lg p-0.5 gap-0.5 border border-emerald-200" data-testid="heatmap-range-tabs">
            {['7d', '30d', '90d', 'all'].map((r) => (
              <button
                key={r}
                onClick={() => { setRange(r); load(r); }}
                className={`px-2 py-0.5 text-[11px] font-semibold rounded transition ${range === r ? 'bg-emerald-600 text-white' : 'text-emerald-800 hover:bg-emerald-100'}`}
                data-testid={`heatmap-range-${r}`}
              >
                {r.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Grid */}
      {sample_size === 0 ? (
        <div className="bg-white/70 rounded-xl p-4 text-xs text-emerald-800 border border-emerald-200" data-testid="heatmap-empty">
          {language === 'hi'
            ? '⏳ अभी कोई WhatsApp read event नहीं — पहले कुछ videos publish करें ताकि data इकट्ठा हो।'
            : '⏳ No WhatsApp read events yet — publish a few videos to start collecting data.'}
        </div>
      ) : (
        <div className="bg-white rounded-xl p-3 border border-emerald-200 overflow-x-auto" data-testid="heatmap-grid">
          <div className="inline-block min-w-full">
            {/* Hour header */}
            <div className="grid" style={{ gridTemplateColumns: '40px repeat(24, minmax(18px, 1fr))' }}>
              <div></div>
              {Array.from({ length: 24 }).map((_, h) => (
                <div key={h} className="text-[9px] text-center text-gray-500 font-mono">
                  {h % 3 === 0 ? `${h.toString().padStart(2, '0')}` : ''}
                </div>
              ))}
            </div>
            {/* Rows */}
            {matrix.map((row, wd) => (
              <div key={wd} className="grid mt-0.5" style={{ gridTemplateColumns: '40px repeat(24, minmax(18px, 1fr))' }}>
                <div className="text-[10px] font-bold text-emerald-800 flex items-center" data-testid={`heatmap-row-${wd}`}>
                  {weekdayLabels[wd]}
                </div>
                {row.map((cell, hr) => (
                  <div
                    key={hr}
                    className={`h-5 mx-px rounded-sm flex items-center justify-center text-[8px] font-mono cursor-default ${cellColor(cell.reads, peak_reads)}`}
                    title={`${weekdayLabels[wd]} ${hr.toString().padStart(2, '0')}:00 IST — ${cell.reads} reads (${cell.share_pct}%)`}
                    data-testid={`heatmap-cell-${wd}-${hr}`}
                  >
                    {cell.reads > 0 && cell.reads === peak_reads ? '★' : ''}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between flex-wrap gap-3 mt-3">
        <div className="text-[11px] text-emerald-700">
          {sample_size > 0 && (
            <>
              {language === 'hi' ? 'Sample' : 'Sample'}: <strong>{sample_size}</strong> reads ·{' '}
              {language === 'hi' ? 'Peak' : 'Peak'}: <strong>{peak_reads}</strong>
              {note && <span className="ml-2 text-orange-700">⚠ {note}</span>}
            </>
          )}
        </div>
        {ts && ts.length > 0 && (
          <button
            onClick={openScheduleModal}
            className="px-3 py-1.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-xs font-bold rounded-lg shadow-sm hover:shadow-md transition disabled:opacity-50"
            data-testid="schedule-this-week-btn"
            disabled={completedJobs.length === 0}
            title={completedJobs.length === 0 ? (language === 'hi' ? 'पहले video generate करें' : 'Generate a video first') : ''}
          >
            📅 {language === 'hi' ? 'इस week के Top Slots schedule करें' : 'Schedule This Week\'s Top Slots'}
          </button>
        )}
      </div>

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => !scheduling && setShowScheduleModal(false)}
          data-testid="schedule-week-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-4 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-black" style={{ fontFamily: 'Outfit' }}>
                  📅 {language === 'hi' ? 'Top Slots पर Schedule करें' : 'Schedule at Top Slots'}
                </h3>
                <button
                  onClick={() => !scheduling && setShowScheduleModal(false)}
                  className="text-white text-2xl leading-none disabled:opacity-50"
                  disabled={scheduling}
                  data-testid="schedule-week-close-btn"
                >×</button>
              </div>
            </div>
            <div className="p-4 space-y-3">
              {/* Video picker */}
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-1">
                  {language === 'hi' ? 'कौन सा video schedule करना है?' : 'Which video to schedule?'}
                </p>
                <select
                  value={selectedJobId}
                  onChange={(e) => setSelectedJobId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  data-testid="schedule-week-video-select"
                  disabled={scheduling}
                >
                  <option value="">— {language === 'hi' ? 'चुनें' : 'pick a video'} —</option>
                  {completedJobs.map((j) => (
                    <option key={j.id} value={j.id}>
                      {j.title || j.id.slice(-12)} ({j.showcase_template_id || 'custom'})
                    </option>
                  ))}
                </select>
              </div>

              {/* Slot count picker */}
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-1">
                  {language === 'hi' ? 'कितने slots?' : 'How many slots?'}
                </p>
                <div className="inline-flex bg-slate-100 rounded p-0.5 gap-0.5">
                  {[3, 5, 7, 10, 14].map((n) => (
                    <button
                      key={n}
                      onClick={() => reloadSlots(n)}
                      disabled={scheduling}
                      className={`px-2 py-1 text-xs font-semibold rounded ${slotCount === n ? 'bg-emerald-600 text-white' : 'text-slate-700 hover:bg-slate-200'}`}
                      data-testid={`slot-count-${n}`}
                    >{n}</button>
                  ))}
                </div>
              </div>

              {/* Slots preview */}
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-1">
                  {language === 'hi' ? 'Upcoming Slots Preview' : 'Upcoming Slots Preview'}
                </p>
                <div className="bg-slate-50 rounded-lg p-2 max-h-48 overflow-y-auto space-y-1 border" data-testid="schedule-week-slots-preview">
                  {topSlots.length === 0 ? (
                    <p className="text-xs text-gray-500">{language === 'hi' ? 'Loading…' : 'Loading…'}</p>
                  ) : (
                    topSlots.map((s, i) => (
                      <div key={i} className="text-[11px] flex items-center justify-between bg-white px-2 py-1 rounded">
                        <span>
                          <strong>{language === 'hi' ? s.label_hi : s.label}</strong>
                          {s.fallback && <span className="ml-1 text-amber-700">(fallback)</span>}
                        </span>
                        <span className="text-gray-500">{new Date(s.scheduled_at).toLocaleDateString()}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Result */}
              {scheduleResult && (
                <div
                  className={`text-sm px-3 py-2 rounded ${scheduleResult.type === 'success' ? 'bg-green-50 text-green-800' : scheduleResult.type === 'partial' ? 'bg-amber-50 text-amber-800' : 'bg-red-50 text-red-800'}`}
                  data-testid="schedule-week-result"
                >
                  {scheduleResult.text}
                </div>
              )}

              {/* Action */}
              <div className="flex gap-2 pt-2 border-t">
                <button
                  onClick={() => setShowScheduleModal(false)}
                  disabled={scheduling}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-semibold disabled:opacity-50"
                  data-testid="schedule-week-cancel-btn"
                >
                  {language === 'hi' ? 'बंद करें' : 'Close'}
                </button>
                <button
                  onClick={scheduleAll}
                  disabled={scheduling || !selectedJobId || topSlots.length === 0}
                  className="flex-1 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-bold rounded-lg disabled:opacity-50"
                  data-testid="schedule-week-confirm-btn"
                >
                  {scheduling
                    ? (language === 'hi' ? `Scheduling… (${topSlots.length})` : `Scheduling… (${topSlots.length})`)
                    : (language === 'hi' ? `🚀 ${topSlots.length} Schedules बनाएं` : `🚀 Create ${topSlots.length} Schedules`)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
